import React, { memo } from 'react';
import {
    Box,
    Typography,
    Paper,
    Skeleton,
    alpha,
    useTheme,
} from '@mui/material';
import {
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    Legend,
} from 'recharts';
import { EventAvailable } from '@mui/icons-material';
import { EmployeeAttendanceSummary, CurrentMonthInfo } from '../../types/dashboardTypes';
import {
    DashboardCard,
    DashboardEmptyState,
    DashboardSkeletonCard,
    PANEL_SKELETON_SX,
    useDashboardAccent,
} from './DashboardCard';

interface MyAttendanceRecordCardProps {
    data?: EmployeeAttendanceSummary;
    currentMonth?: CurrentMonthInfo;
    loading?: boolean;
}

// Custom tooltip for the chart
const CustomTooltip = ({ active, payload }: any) => {
    const theme = useTheme();
    if (active && payload && payload.length) {
        return (
            <Paper
                sx={{
                    p: 1.5,
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    boxShadow: 3,
                }}
            >
                <Typography variant="subtitle2" fontWeight={600}>
                    {payload[0].name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {payload[0].value} day{payload[0].value !== 1 ? 's' : ''}
                </Typography>
            </Paper>
        );
    }
    return null;
};

/**
 * My Attendance Record Card
 * Displays employee's attendance summary for the current month as a donut chart
 * Reusable for any employee role (Teacher, Staff, etc.)
 */
const MyAttendanceRecordCard: React.FC<MyAttendanceRecordCardProps> = memo(({
    data,
    currentMonth,
    loading = false,
}) => {
    const theme = useTheme();
    const leaveAccent = useDashboardAccent('cyan');
    const halfDayAccent = useDashboardAccent('violet');

    // Define colors for each status
    const statusColors = {
        present: theme.palette.success.main,
        late: theme.palette.warning.main,
        absent: theme.palette.error.main,
        leave: leaveAccent.main,
        halfDay: halfDayAccent.main,
    };

    // Prepare chart data
    const chartData = data ? [
        { name: 'Present', value: data.present, color: statusColors.present },
        { name: 'Late', value: data.late, color: statusColors.late },
        { name: 'Absent', value: data.absent, color: statusColors.absent },
        { name: 'On Leave', value: data.leave, color: statusColors.leave },
        { name: 'Half Day', value: data.halfDay, color: statusColors.halfDay },
    ].filter(item => item.value > 0) : [];

    const totalDays = data?.totalWorkingDays || 0;
    const hasData = chartData.length > 0;

    // Loading skeleton
    if (loading) {
        return (
            <DashboardSkeletonCard sx={PANEL_SKELETON_SX}>
                <Skeleton variant="text" width={180} height={28} sx={{ mb: 2 }} />
                <Skeleton variant="circular" width={200} height={200} sx={{ mx: 'auto', my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                    {[1, 2, 3, 4].map((i) => (
                        <Skeleton key={i} variant="rounded" width={60} height={20} />
                    ))}
                </Box>
            </DashboardSkeletonCard>
        );
    }

    return (
        <DashboardCard
            accent="primary"
            icon={EventAvailable}
            title="My Attendance"
            subtitle={currentMonth ? `${currentMonth.monthName} ${currentMonth.year}` : 'Current month'}
        >
            {/* Content Area */}
            <Box sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>

            {/* Chart or Empty State */}
            {hasData ? (
                <>
                    <ResponsiveContainer width="100%" height={220}>
                        <PieChart>
                            <Pie
                                data={chartData}
                                cx="50%"
                                cy="50%"
                                innerRadius={55}
                                outerRadius={85}
                                paddingAngle={3}
                                dataKey="value"
                            >
                                {chartData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                            </Pie>
                            <RechartsTooltip content={<CustomTooltip />} />
                            <Legend
                                verticalAlign="bottom"
                                height={36}
                                formatter={(value) => (
                                    <span style={{ color: theme.palette.text.primary, fontSize: '0.8rem' }}>
                                        {value}
                                    </span>
                                )}
                            />
                        </PieChart>
                    </ResponsiveContainer>

                    {/* Total days indicator */}
                    <Box sx={{ textAlign: 'center', mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Total Working Days: <strong>{totalDays}</strong>
                        </Typography>
                    </Box>
                </>
            ) : (
                <DashboardEmptyState
                    size="roomy"
                    accent="primary"
                    icon={EventAvailable}
                    title="No Attendance Records"
                    description={`Your attendance for ${currentMonth?.monthName || 'this month'} will appear here once recorded.`}
                />
            )}
            </Box>
        </DashboardCard>
    );
});

MyAttendanceRecordCard.displayName = 'MyAttendanceRecordCard';

export default MyAttendanceRecordCard;
